import classnames from 'classnames';
import React, { useEffect } from 'react';

import MetsIcon from 'commons/components/MetsIcon';
import { scrollToElem, isOutOfViewport } from 'commons/resources/utils/scroll'

interface FormRowProps {
    className?: string,
    /** The children of the FormRow should include the widget or input. */
    children: React.ReactNode,
    disabled?: boolean,
    description?: string,
    error?: string,
    fieldIcon?: any,
    idPrefix?: string,
    inputId?: string,
    label?: string | string[],
    /** Some widgets may render their own error icon, or not require the icon, such as the ItemSelect, Checkboxes or Radio Buttons. */
    renderErrorIcon?: boolean,
    touched?: boolean,
    type?: string
}

function FormRow(props: FormRowProps) {

    const formRowRef = React.createRef<HTMLDivElement>();
    const rowClassName = classnames('form-field-row', `form-field-row-${props.type}`, props.className, { disabled: props.disabled }, { 'has-error': props.error && props.touched });

    // This is so that if the field is no longer in view and an error appears on it, the browser will scroll to the field.
    useEffect(() => {

        if (props.error && props.touched) {
            if (formRowRef.current) {
                const isOut = isOutOfViewport(formRowRef.current);
                if (isOut.any) {
                    scrollToElem(formRowRef.current, 30);
                }
            }
        }

    }, [props.error, props.touched]);

    function renderErrors() {
        if (props.error && props.touched) {
            return (
                <div data-testid={props.idPrefix + "_error"} className="form-field-error">
                    {props.error.split('\n').map((error, index) => {
                        return (<div className="form-field-error-line" key={index}>{error}</div>);
                    })}
                </div>
            );
        }
    }

    function renderDescription() {
        if (props.description && !props.error && !props.touched) {
            return <div className="form-field-description">{props.description}</div>
        }
    }

    function renderErrorIcon() {
        if (props.renderErrorIcon && props.error && props.touched && typeof props.fieldIcon === "undefined") {
            return <MetsIcon name="svg_error" className="form-field-error-icon" />
        }
    }

    function renderCustomInput() {
        if (props.type === "checkbox" || props.type === "radio") {
            return <span className={props.type}></span>;
        } else {
            return;
        }
    }

    let widget = <>
        {props.children}
        {renderCustomInput()}
        {renderErrorIcon()}
        {props.fieldIcon ?? ""}
    </>;

    // If the type is checkbox or radio, the widget should be wrapped with an additional <label>
    if (props.type === 'checkbox' || props.type === 'radio') {
        widget = <label htmlFor={props.inputId}>{widget}</label>;
    }

    return (

        <div
            className={rowClassName}
            data-testid={props.idPrefix + '_formRow'}
            id={props.idPrefix + '_formRow'}
            ref={formRowRef}>
            <label htmlFor={props.inputId} className="form-field-label">
                {props.label}
            </label>
            <div className="form-field-wrapper">
                {widget}
            </div>
            {renderDescription()}
            {renderErrors()}
        </div>

    );

}

FormRow.defaultProps = {
    disabled: false,
    renderErrorIcon: true,
    touched: false,
    type: "text"
};

export default FormRow;